import React from 'react';
import { X, TrendingDown, TrendingUp, DollarSign, Clock, Zap, Target, Sparkles } from 'lucide-react';
import type { IProjectMetrics } from '../types';

interface FutureStateModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMetrics: IProjectMetrics;
  futureMetrics: IProjectMetrics;
  improvements: {
    leadTimeReduction: number;
    costReduction: number;
    efficiencyGain: number;
    annualSaving: number;
  };
}

export const FutureStateModal: React.FC<FutureStateModalProps> = ({
  isOpen,
  onClose,
  currentMetrics,
  futureMetrics,
  improvements,
}) => {
  if (!isOpen) return null;

  const currentLeadTime = parseFloat(currentMetrics.longestPath) || 0;
  const futureLeadTime = parseFloat(futureMetrics.longestPath) || 0;
  const currentEfficiency = parseFloat(currentMetrics.flowEfficiency) || 0;
  const futureEfficiency = parseFloat(futureMetrics.flowEfficiency) || 0;
  const currentCost = currentMetrics.monthlyOpCost || 0;
  const futureCost = futureMetrics.monthlyOpCost || 0;

  const ComparisonCard = ({ 
    title, 
    icon, 
    currentValue, 
    futureValue, 
    unit, 
    improvement, 
    improvementUnit = '%',
    colorClass 
  }: { 
    title: string; 
    icon: React.ReactNode; 
    currentValue: number; 
    futureValue: number; 
    unit: string; 
    improvement: number; 
    improvementUnit?: string;
    colorClass: string;
  }) => (
    <div className="bg-white dark:bg-slate-900 rounded p-6 shadow-lg border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 ${colorClass} rounded`}>
          {icon}
        </div>
        <h3 className="font-bold text-slate-800 dark:text-slate-200 text-lg">{title}</h3>
      </div>
      
      <div className="space-y-4">
        {/* Estado Atual */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600 dark:text-slate-400">Atual</span>
          <span className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {currentValue.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}{unit}
          </span>
        </div>
        
        {/* Seta de Transição */}
        <div className="flex items-center justify-center">
          <div className="h-px w-full bg-gradient-to-r from-slate-200 dark:from-neutral-700 via-slate-400 dark:via-neutral-600 to-slate-200 dark:to-neutral-700" />
          <TrendingDown className="mx-2 text-emerald-500 dark:text-emerald-400" size={24} />
          <div className="h-px w-full bg-gradient-to-r from-slate-200 dark:from-neutral-700 via-slate-400 dark:via-neutral-600 to-slate-200 dark:to-neutral-700" />
        </div>
        
        {/* Estado Futuro */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Cenário Kaizen</span>
          <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {futureValue.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}{unit}
          </span>
        </div>
        
        {/* Melhoria */}
        <div className="pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 uppercase font-bold">Redução</span>
            <div className="flex items-center gap-1">
              <TrendingDown size={16} className="text-emerald-600" />
              <span className="text-lg font-bold text-emerald-600">
                {improvement.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}{improvementUnit}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-slate-50 to-white rounded shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border-2 border-emerald-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded backdrop-blur-sm">
                <Sparkles size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Simulação de Cenário Futuro (Kaizen)</h2>
                <p className="text-emerald-100 text-sm mt-1">Potencial de melhoria do processo</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          
          {/* Cards de COMPARAÇÃO */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <ComparisonCard
              title="Lead Time"
              icon={<Clock className="text-blue-600" size={24} />}
              currentValue={currentLeadTime}
              futureValue={futureLeadTime}
              unit=" min"
              improvement={improvements.leadTimeReduction}
              colorClass="bg-blue-100"
            />
            
            <ComparisonCard
              title="Eficácia Operacional"
              icon={<Zap className="text-purple-600" size={24} />}
              currentValue={currentEfficiency}
              futureValue={futureEfficiency}
              unit="%"
              improvement={improvements.efficiencyGain}
              improvementUnit=" p.p."
              colorClass="bg-purple-100"
            />
            
            <ComparisonCard
              title="Custo Mensal"
              icon={<DollarSign className="text-amber-600" size={24} />}
              currentValue={currentCost}
              futureValue={futureCost}
              unit=""
              improvement={improvements.costReduction}
              colorClass="bg-amber-100"
            />
          </div>

          {/* Economia Anual Destacada */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded backdrop-blur-sm">
                  <Target size={32} />
                </div>
                <div>
                  <p className="text-emerald-100 text-sm uppercase font-bold tracking-wide">Economia Anual Projetada</p>
                  <p className="text-5xl font-bold mt-1">
                    R$ {improvements.annualSaving.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-emerald-100 text-sm">Aplicando melhorias Kaizen:</p>
                <ul className="text-xs text-emerald-100 mt-2 space-y-1 text-left">
                  <li>âœ“ Redução de 50% da espera</li>
                  <li>âœ“ Melhoria de 10% nos gargalos</li>
                  <li>âœ“ Otimização do fluxo</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Metodologia */}
          <div className="mt-6 p-4 bg-slate-100 rounded border border-slate-200">
            <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
              <Target size={16} />
              Metodologia de Simulação
            </h4>
            <ul className="text-xs text-slate-600 space-y-1">
              <li>â¬¢ <strong>Redução de Espera:</strong> Onde TP &gt; TRA, reduz espera em 50% (eliminação de filas e lotes menores)</li>
              <li>â¬¢ <strong>Melhoria de Produtividade:</strong> Recursos com ocupação &gt; 90% ganham 10% de Eficiência (automação/treinamento)</li>
              <li>â¬¢ <strong>Impacto no Lead Time:</strong> Caminho crítico recalculado com novos tempos</li>
              <li>â¬¢ <strong>Economia:</strong> Redução de custo operacional  12 meses</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-100 p-4 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-600 text-white rounded font-medium hover:bg-emerald-700 transition-colors shadow-md"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};


